import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Building2, Stethoscope, CalendarClock, Activity, FileLock2, Layers,
} from 'lucide-react';
import CommonLoader from '../../components/CommonLoader';
import Pagination from '../../components/Pagination';
import BreakGlassModal from './BreakGlassModal';
import { ACTIVITY_KINDS, KIND_MAP, formatDate, formatDateTime } from './mocCommon';
import useCanManage from '../../hooks/useCanManage';

// PLATFORM ACTIVITY — the one view that is genuinely MOC-level.
//
// Nothing in the tenant app can show this. A hospital sees its own appointments;
// only the platform can say "this person was at St Mary's on Tuesday and at the
// city lab on Thursday", because only the platform holds the identity that joins
// the two. That is why this survived the redesign as its own tab instead of
// being folded into Medical History, which is per-hospital by construction.
//
// WHAT IS AND IS NOT SHOWN. The timeline is METADATA: which hospital, when, what
// kind of event, its status. No diagnosis, no medicine, no result value ever
// reaches this screen — that is what break-glass is for, and it costs an audit
// row with a written reason every single time, refusals included.
//
// Extracted out of MocPatientDetail when that page became a six-tab profile:
// the shell now owns the header, the hospital picker and the tab rail, and this
// owns everything cross-hospital.

const DEFAULT_LIMIT = 20;

// The uppercase local alias is this repo's convention for a component held in a
// prop: eslint has no react plugin here, so a JSX-only reference doesn't count
// as a use — only `varsIgnorePattern: ^[A-Z_]` saves it.
const KindChip = ({ id, label, icon, num, active, onClick }) => {
    const Icon = icon;
    return (
        <button
            type="button"
            data-testid={`moc-kind-${id}`}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${active
                ? 'bg-[#4ABDBD] text-white border-[#4ABDBD] shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD] hover:text-[#2D8A8A]'
                }`}
        >
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${active ? 'bg-white/25' : 'bg-gray-100 text-gray-500'}`}>
                {num ?? 0}
            </span>
        </button>
    );
};

const TimelineRow = ({ row, onBreakGlass, canBreakGlass }) => {
    const { t } = useTranslation();
    const meta = KIND_MAP[row.kind];
    const Icon = meta?.icon || Activity;
    return (
        <div
            data-testid={`moc-timeline-${row.kind}-${row.ref}`}
            className="flex items-start gap-3 rounded-xl border border-gray-100 p-3 hover:shadow-sm transition-shadow"
        >
            <div className={`p-2 rounded-lg flex-shrink-0 ${meta?.tone || 'bg-gray-100 text-gray-500'}`}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p className="text-sm font-semibold text-gray-800 min-w-0">{row.title}</p>
                    {row.status && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold flex-shrink-0">
                            {row.status}
                        </span>
                    )}
                </div>
                {row.subtitle && <p className="text-xs text-gray-500 mt-0.5">{row.subtitle}</p>}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-gray-400">
                    <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" />{row.hospitalName || t('moc.hr_detail_hospital', 'Hospital')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" />{formatDateTime(row.at)}
                    </span>
                    {/* row.ref (the appointment / order / study id) stays in
                        the test id and the break-glass payload only: no system
                        id is drawn on screen (owner rule, 29 Sep 2026). */}
                </div>
            </div>
            {canBreakGlass && (
            <button
                type="button"
                data-testid={`moc-breakglass-${row.ref}`}
                onClick={() => onBreakGlass(row)}
                title="Opens a logged, audited read of the clinical record"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#D7A05C]/60 text-[#8a5f26] bg-[#D7A05C]/10 text-[11px] font-semibold hover:bg-[#D7A05C]/20 transition-colors cursor-pointer flex-shrink-0"
            >
                <FileLock2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">View clinical detail</span>
            </button>
            )}
        </div>
    );
};

const HospitalRow = ({ hospital, isCurrent, onPick, hospitalWord }) => (
    <li>
        <button
            type="button"
            data-testid={`moc-hospital-row-${hospital.tenantId}`}
            onClick={() => onPick(hospital.tenantId)}
            className={`w-full text-left rounded-xl px-3 py-2 transition-colors cursor-pointer ${isCurrent ? 'bg-[#4ABDBD]/10 ring-1 ring-[#4ABDBD]/30' : 'bg-gray-50 hover:bg-gray-100'}`}
        >
            <p className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{hospital.hospitalName || hospitalWord}</span>
                {isCurrent && (
                    <span className="ml-auto rounded-full bg-[#4ABDBD] px-2 py-0.5 text-[10px] font-bold text-white flex-shrink-0">
                        Viewing
                    </span>
                )}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5 pl-6">
                {hospital.linkSource === 'registration' ? 'Registered here' : 'Has a patient number here'}
                {hospital.linkedAt ? ` · ${formatDate(hospital.linkedAt)}` : ''}
                {/* The HOSPITAL's own number, when the API sends it. Never the
                    person id: it is the same string under every hospital since
                    one-patient-row, so printing it per row read as "they have
                    four ids" when they have one. */}
                {(hospital.hospitalPatientCode || hospital.tenantPatientCode)
                    ? <>{' · '}<span className="font-mono">{hospital.hospitalPatientCode || hospital.tenantPatientCode}</span></>
                    : null}
            </p>
        </button>
    </li>
);

const MocActivityPanel = ({
    mocAccountId, patientName, hospitals, doctors,
    activity, activityLoading, activityError,
    kind, onKindChange, limit, offset, onPageChange,
    currentTenantId, onPickHospital,
}) => {
    // Break-glass is a WRITE on the API (patients.manage — it records a security
    // audit row), so the button is not offered without the key.
    const canManage = useCanManage('patients.manage');
    const { t } = useTranslation();
    const hospitalWord = t('moc.hr_detail_hospital', 'Hospital');
    const doctorWord = t('patientappointment.doctor_fallback', 'Doctor');
    const [breakGlassRow, setBreakGlassRow] = useState(null);

    const counts = activity?.counts || {};
    const timeline = activity?.timeline || [];
    const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
    // Selecting a hospital FILTERS this whole panel (owner, 2026-09-02): the
    // fetch carries tenantId, so doctors, counts and timeline all describe the
    // selected hospital — the headers below say which one.
    const currentHospitalName = useMemo(() => {
        const h = hospitals.find((x) => x.tenantId === currentTenantId);
        return h ? (h.hospitalName || hospitalWord) : null;
    }, [hospitals, currentTenantId, hospitalWord]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="moc-activity-panel">
            <div className="space-y-4 xl:col-span-1">
                <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
                    <h2 className="text-sm font-bold text-gray-700 mb-1">
                        Hospitals ({hospitals.length})
                    </h2>
                    <p className="text-[11px] text-gray-400 mb-3">
                        Where this one profile is known. Each hospital keeps its own number for the patient and its
                        own records — pick one to filter the doctors, counts and timeline here.
                    </p>
                    {hospitals.length === 0 ? (
                        <p className="text-xs text-gray-400">No hospital has given this patient a number yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {hospitals.map((h) => (
                                <HospitalRow
                                    key={h.tenantId}
                                    hospital={h}
                                    isCurrent={h.tenantId === currentTenantId}
                                    onPick={onPickHospital}
                                    hospitalWord={hospitalWord}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5">
                    <h2 className="text-sm font-bold text-gray-700 mb-1">
                        Doctors ({doctors.length})
                    </h2>
                    <p className="text-[11px] text-gray-400 mb-3">
                        {currentHospitalName
                            ? `Every doctor this patient is under at ${currentHospitalName}.`
                            : 'Every doctor this patient is under, across all connected hospitals.'}
                    </p>
                    {activityLoading && doctors.length === 0 ? (
                        <div className="py-4 flex justify-center"><CommonLoader /></div>
                    ) : doctors.length === 0 ? (
                        <p className="text-xs text-gray-400">No doctor relationships recorded.</p>
                    ) : (
                        <ul className="space-y-2">
                            {doctors.map((d) => (
                                <li key={`${d.tenantId}-${d.doctorId}`} className="rounded-xl bg-gray-50 px-3 py-2">
                                    <p className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                                        <Stethoscope className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <span className="truncate">{d.name || doctorWord}</span>
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-0.5 pl-6">
                                        {[d.specialty, d.hospitalName || hospitalWord].filter(Boolean).join(' · ') || '—'}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 xl:col-span-2">
                <div className="flex items-center gap-3 mb-1">
                    <div className="p-2 bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] rounded-lg shadow-sm">
                        <Activity className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-gray-700" data-testid="moc-activity-heading">
                            {currentHospitalName ? `Activity at ${currentHospitalName}` : 'Activity across every connected hospital'}
                        </h2>
                        <p className="text-[11px] text-gray-400">
                            Metadata only — what happened, where and when. Clinical content is behind break-glass,
                            which records who read it and why.
                        </p>
                    </div>
                </div>

                {/* The chip counts describe the whole history AT THE SELECTED
                    HOSPITAL (the fetch carries tenantId) but stay unfiltered
                    by kind on purpose, so the chips never lie about what is
                    behind them. */}
                <div className="flex flex-wrap gap-2 mt-4 mb-4">
                    <KindChip
                        id="all"
                        label="All"
                        icon={Layers}
                        num={counts.total}
                        active={kind === ''}
                        onClick={() => onKindChange('')}
                    />
                    {ACTIVITY_KINDS.map((k) => (
                        <KindChip
                            key={k.id}
                            id={k.id}
                            label={k.label}
                            icon={k.icon}
                            num={counts[k.id]}
                            active={kind === k.id}
                            onClick={() => onKindChange(k.id)}
                        />
                    ))}
                </div>

                {activityLoading ? (
                    <div className="py-10 flex justify-center"><CommonLoader /></div>
                ) : activityError ? (
                    <div className="text-center py-10">
                        <p className="text-sm text-red-600">{activityError}</p>
                    </div>
                ) : timeline.length === 0 ? (
                    <div data-testid="moc-timeline-empty" className="text-center py-12">
                        <Activity className="w-9 h-9 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                            {kind ? 'Nothing of this kind for this identity.' : 'No activity recorded for this identity yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {timeline.map((row) => (
                            <TimelineRow
                                key={`${row.kind}-${row.ref}`}
                                row={row}
                                onBreakGlass={setBreakGlassRow}
                                canBreakGlass={canManage}
                            />
                        ))}
                    </div>
                )}

                {!activityLoading && (activity?.total || 0) > 0 && (
                    <div className="mt-4">
                        <Pagination
                            totalItems={activity.total}
                            currentPage={currentPage}
                            itemsPerPage={limit}
                            onPageChange={onPageChange}
                        />
                    </div>
                )}
            </div>

            {breakGlassRow && (
                <BreakGlassModal
                    mocAccountId={mocAccountId}
                    patientName={patientName}
                    row={breakGlassRow}
                    onClose={() => setBreakGlassRow(null)}
                />
            )}
        </div>
    );
};

export { DEFAULT_LIMIT };
export default MocActivityPanel;
